<template>
    <div
        v-if="!item.module_data?.promotion"
        class="c-minicart-product cart__product"
        :class="{ 'is-refused': item.is_refused }"
    >
        <a class="c-minicart-product__img" :href="item.link" :title="item.name">
            <span class="is-action" v-if="actionPercentage">
                <span>- {{ actionPercentage }}</span>
                <small>%</small>
            </span>
            <img class="c-img" :src="thumbnail(item.image, 'xs')" :alt="item.name" />
        </a>

        <div class="c-minicart-product__name" v-if="isGift">
            <span>{{ "+ " + $t("MinicartItem.gift") + " : " }}</span>
            <strong>
                {{ item.name }}
                {{
                    item.promotion.original_price
                        ? $t("MinicartItem.gift2", {
                              price: formatPrice(item.promotion.original_price),
                          })
                        : ""
                }}
            </strong>
        </div>

        <div class="c-minicart-product__name" v-else>
            <a class="c-link c-link--inherit" :href="item.link" :title="item.name">
                {{ item.name }}
            </a>
        </div>

        <div class="c-minicart-product__quantity" v-if="!item.is_not_saleable && !isGift">
            <r-spinner class="c-form__quantity" ref="rSpinner" :data-tolerance="item.tolerance" :data-free-input="item.free_input || null" :data-step-choice="item.step_choice || null" :data-step-texts="item.step_choice ? JSON.stringify(stepTexts) : null">
                <button type="button" class="c-btn c-btn--cart is-decrement" decrement>
                    <i class="ico ico-minus"></i>
                </button>

                <input
                    type="text"
                    class="c-form__input"
                    :value="quantity"
                    :append="item.unit"
                    :step="item.step"
                    :min="item.min"
                    :disabled="item.disable_manual_quantity_change"
                    autocomplete="off"
                />

                <button
                    type="button"
                    class="c-btn c-btn--cart is-increment"
                    increment
                    @mousedown.capture="onIncrementClick"
                >
                    <i class="ico ico-plus"></i>
                </button>
            </r-spinner>
        </div>

        <span class="c-minicart-product__price is-free" v-if="isGift">
            <span class="c-price">{{ $t("MinicartItem.free") }}</span>
        </span>

        <span
            v-else
            class="c-minicart-product__price"
            :class="{ 'has-action': item.is_action || item.promotion_discount != 0 }"
        >
            <small v-if="item.promotion_discount != 0" class="sc-old" v-html="oldPrice"></small>

            <span class="c-price" v-html="formatPrice(actualPrice)"></span>

            <span
                v-if="item.deposit_price"
                class="c-deposit-info"
                v-html="$t('MinicartItem.deposit', { price: formatPrice(item.deposit_price) })"
            ></span>
        </span>

        <div v-if="cartDiscountSetItems.length" class="c-minicart-product__set-items">
            <a
                v-for="setItem in cartDiscountSetItems"
                :key="setItem.id"
                :href="setItem.link"
                class="c-minicart-product__set-item"
            >
                <img class="is-image" :src="thumbnail(setItem.image, 'xs')" :alt="setItem.name" />

                <span class="is-name">{{ setItem.name }}</span>

                <span class="is-price">
                    {{ setItem.quantity }} {{ setItem.unit }} x {{ formatPrice(setItem.price) }}
                </span>
            </a>
        </div>

        <div class="c-minicart-product__delete">
            <a
                v-if="!isGift && !item.is_not_saleable"
                href="#"
                class="c-btn"
                :class="{ 'is-loading': isUpdatingQuantity }"
                :title="$t('MinicartItem.remove')"
                :ref="'remove_' + item.cart_id"
                @click.prevent="remove(item.cart_id)"
            >
                <i aria-hidden="true" class="ico ico-close"></i>
            </a>

            <a
                v-if="item.is_not_saleable && !item.is_refused"
                href="#"
                class="c-btn"
                :class="{ 'is-loading': isUpdatingQuantity }"
                :title="$t('MinicartItem.removeGift')"
                :ref="'remove_gift_' + item.cart_id"
                @click.prevent="removeGift(item.cart_id)"
            >
                <i aria-hidden="true" class="ico ico-close"></i>
            </a>

            <a
                v-if="item.is_not_saleable && item.is_refused"
                href="#"
                class="c-btn"
                :class="{ 'is-loading': isUpdatingQuantity }"
                :title="$t('MinicartItem.returnGift')"
                :ref="'return_gift_' + item.cart_id"
                @click.prevent="returnGift(item.cart_id)"
            >
                <i aria-hidden="true" class="ico ico-repeat"></i>
            </a>
        </div>

        <div v-if="gifts.length" class="c-minicart-product__gifts">
            <a
                v-for="gift in gifts"
                :key="gift.cart_id"
                :href="gift.full_link"
                class="c-gift-product-thumb-wrapper"
            >
                <div class="c-gift-product-thumb" :class="{ 'is-refused': gift.is_refused }">
                    <button
                        v-if="!gift.is_refused"
                        type="button"
                        class="c-gift-product-thumb__delete js-remove-gift"
                        :title="$t('MinicartItem.removeGift')"
                        @click.prevent.stop="removeGift(gift.cart_id)"
                    >
                        <i class="ico ico-close"></i>
                    </button>
                    <button
                        v-if="gift.is_refused"
                        type="button"
                        class="c-gift-product-thumb__delete js-return-gift"
                        :title="$t('MinicartItem.returnGift')"
                        @click.prevent.stop="returnGift(gift.cart_id)"
                    >
                        <i class="ico ico-repeat"></i>
                    </button>
                    <div class="c-gift-product-thumb__image">
                        <div class="sc-icon">
                            <i class="ico ico-gift"></i>
                        </div>

                        <img class="c-img" :src="thumbnail(gift.image, 'xs-cutout')" :alt="gift.name" />
                    </div>
                    <div class="c-gift-product-thumb__title is-desktop">
                        {{ $t("Minicart.giftProductTitleDesktop") }}
                    </div>
                    <div class="c-gift-product-thumb__title is-mobile">
                        {{ $t("Minicart.giftProductTitleMobile") }}
                    </div>
                    <div class="c-gift-product-thumb__name">
                        <span v-if="gift.quantity > 1" class="c-gift-product-thumb__qty">{{ gift.quantity }}x</span>
                        {{ gift.name }}
                    </div>
                    <div class="c-gift-product-thumb__price">
                        <span>{{ $t("Minicart.giftProductPriceLabel") }}</span>
                        {{ formatPrice(gift.promotion.original_price) }}
                    </div>
                </div>
            </a>
        </div>
    </div>
</template>

<script>
import { thumbnail } from "Functions/thumbnail";
import { formatPrice } from "FrontendJsAssets/common/format";

export default {
    props: ["item", "isUpdatingQuantity", "gifts"],
    data() {
        return {
            pricesWithVat: window.Eshop.options.pricesWithVat,
            quantity: 1,
        };
    },
    methods: {
        formatPrice(price) {
            return formatPrice(price);
        },
        thumbnail(image, format) {
            return thumbnail(image, format);
        },
        remove(cartId) {
            if (this.isUpdatingQuantity) {
                return;
            }

            this.$emit("remove", cartId);
        },
        removeGift(cartId) {
            if (this.isUpdatingQuantity) {
                return;
            }

            this.$emit("removeGift", cartId);
        },
        returnGift(cartId) {
            if (this.isUpdatingQuantity) {
                return;
            }

            this.$emit("returnGift", cartId);
        },
        updateQuantity() {
            const quantity = this.rSpinner.getParsedValue();
            const oldValue = this.rSpinner.getParsedOldValue();
            const spinDirection = this.rSpinner.spinDirection;
            const itemMax = parseFloat(this.item.max) || 999;

            if (quantity == oldValue && quantity == this.rSpinner.min && spinDirection == "down") {
                this.$emit("remove", this.item.cart_id);
                return;
            }

            // Check if trying to go above max - cap at max
            if (quantity > itemMax) {
                this.showMaxQuantityMessage();
                // Reset to max value
                const formattedValue = `${itemMax} ${this.item.unit}`;
                this.rSpinner.input.value = formattedValue;
                this.rSpinner.input.setAttribute("value", formattedValue);
                this.quantity = itemMax;
                this.$emit("updateQuantity", this.item.cart_id, this.quantity);
                return;
            }

            // Show message when at max and trying to increment
            if (quantity >= itemMax && spinDirection === "up") {
                this.showMaxQuantityMessage();
            }

            this.quantity = this.rSpinner.value;
            this.$emit("updateQuantity", this.item.cart_id, this.quantity);
        },
        onIncrementClick(event) {
            const itemMax = parseFloat(this.item.max) || 999;
            const currentQuantity = parseFloat(this.quantity) || 0;

            // If at max, prevent the increment entirely
            if (currentQuantity >= itemMax) {
                event.stopPropagation();
                event.preventDefault();
                this.showMaxQuantityMessage();
            }
        },
        onSpinStart() {
            const itemMax = parseFloat(this.item.max) || 999;
            const currentQuantity = parseFloat(this.quantity) || 0;

            // If at max and trying to increment, immediately reset value
            if (currentQuantity >= itemMax && this.rSpinner.spinDirection === "up") {
                this.showMaxQuantityMessage();
                // Immediately reset to prevent visual change
                requestAnimationFrame(() => {
                    const formattedValue = `${this.quantity} ${this.item.unit}`;
                    this.rSpinner.input.value = formattedValue;
                    this.rSpinner.input.setAttribute("value", formattedValue);
                });
            }
        },
        spinningQuantity() {
            const quantity = this.rSpinner.getParsedValue();
            const itemMax = parseFloat(this.item.max) || 999;

            // Only block and show error when trying to go ABOVE max
            if (quantity > itemMax && this.rSpinner.spinDirection === "up") {
                this.showMaxQuantityMessage();
                // Reset to max value, not old value
                const formattedValue = `${itemMax} ${this.item.unit}`;
                this.rSpinner.input.value = formattedValue;
                this.rSpinner.input.setAttribute("value", formattedValue);
                return;
            }

            // Show message when at max and trying to increment (but don't reset value)
            if (quantity >= itemMax && this.rSpinner.spinDirection === "up") {
                this.showMaxQuantityMessage();
            }

            this.$emit("spinningQuantity", this.item.cart_id);
        },
        showMaxQuantityMessage() {
            if (this.item.custom_max_message) {
                Eshop.tippyAlert(
                    this.rSpinner.input,
                    this.item.custom_max_message,
                    {
                        theme: "warning",
                        zIndex: 102,
                    },
                    20 * 1000
                );
            }
        },
    },
    computed: {
        // step quantity dialog texts (sk.json)
        stepTexts() {
            return {
                title: this.$t("Swal.StepQuantity.title"),
                text: this.$t("Swal.StepQuantity.text"),
                entered: this.$t("Swal.StepQuantity.entered"),
            };
        },
        actualPrice() {
            const newUnitPrice = this.pricesWithVat
                ? this.item.price_vat - this.item.unit_promotion_discount_vat
                : this.item.price - this.item.unit_promotion_discount;

            return this.item.quantity * newUnitPrice;
        },
        oldPrice() {
            return this.formatPrice(
                this.item.quantity * (this.pricesWithVat ? this.item.price_vat : this.item.price)
            );
        },
        actionPercentage() {
            //? https://riesenia.atlassian.net/browse/LNS-63?focusedCommentId=91497
            return 0;

            if (this.isGift || !this.item.is_action) {
                return 0;
            }

            const discount = this.item.old_price - this.item.price;

            return Math.round((discount / this.item.old_price) * 100);
        },
        isGift() {
            return false;
            // return this.item.promotion && this.item.promotion.promotion_id == 2;
        },
        cartDiscountSetItems() {
            return this.item.cart_discount_set_items || [];
        },
        rSpinner() {
            return this.$refs.rSpinner;
        },
    },
    created() {
        this.quantity = this.item.quantity;
    },
    mounted() {
        if (this.rSpinner) {
            this.$nextTick(() => {
                this.rSpinner.addEventListener("start", this.onSpinStart);
                this.rSpinner.addEventListener("spin", this.spinningQuantity);
                this.rSpinner.addEventListener("stop", this.updateQuantity);
                this.rSpinner.addEventListener("changed", this.updateQuantity);

                // Ensure spinner has correct value after web component is fully initialized
                // Directly set input value to bypass spinner's rounding logic
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (
                            this.rSpinner &&
                            this.rSpinner.input &&
                            this.rSpinner.classList.contains("is-loaded")
                        ) {
                            const formattedValue = `${this.quantity} ${this.item.unit}`;
                            this.rSpinner.input.value = formattedValue;
                            this.rSpinner.input.setAttribute("value", formattedValue);
                        }
                    });
                });
            });
        }
    },
    beforeDestroy() {
        if (this.rSpinner) {
            this.rSpinner.removeEventListener("start", this.onSpinStart);
            this.rSpinner.removeEventListener("spin", this.spinningQuantity);
            this.rSpinner.removeEventListener("stop", this.updateQuantity);
            this.rSpinner.removeEventListener("changed", this.updateQuantity);
        }
    },
    watch: {
        item: {
            deep: true,
            handler() {
                this.quantity = this.item.quantity;

                this.$nextTick(() => {
                    const rSpinnerEl = this.rSpinner;

                    if (
                        rSpinnerEl &&
                        rSpinnerEl.input &&
                        rSpinnerEl.classList &&
                        rSpinnerEl.classList.contains("is-loaded")
                    ) {
                        // Directly set input value to bypass spinner's rounding logic
                        const formattedValue = `${this.quantity} ${this.item.unit}`;
                        rSpinnerEl.input.value = formattedValue;
                        rSpinnerEl.input.setAttribute("value", formattedValue);
                    }
                });
            },
        },
    },
};
</script>
